import { useEffect, useState } from 'react';
import { Tag, Button, Drawer, Flex, Divider } from 'antd';
import {
  EditOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useForm, useModal, useSelect, useSimpleList } from '@refinedev/antd';
import { useNavigation, useOne, useTranslate } from '@refinedev/core';
import dayjs from 'dayjs';

import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import apiEndpoint from 'core/common/apiEndPoints';
import resourceName from 'core/common/resourceName';
import constants from 'core/constants/Constants';
import openNotificationWithIcon from 'core/utils/notification';
import RulePlanForm from 'pages/ruleChange/rulePlan/RulePlanForm';
import { useTokenContext } from 'contexts/resource/ResourceContext';
import CustomModal from 'components/modal/modal';
import getFooterButton from 'core/utils/getFooterButton';

const transformPlanValues = (values: any) => {
  const [start, end] = values?.rule_plans_date || [];
  return {
    ...values,
    rule_plan_start_date: start ? dayjs(start).format('YYYY-MM-DD') : undefined,
    rule_plan_end_date: end ? dayjs(end).format('YYYY-MM-DD') : undefined,
  };
};

const RulePlanStrip = ({ pageType, refetch }) => {
  const t = useTranslate();
  const { rulePlanDataObj, setRulePlanDataObj } = useRulesContext();
  const { userDetails } = useTokenContext();
  const { list } = useNavigation();
  const [editing, setEditing] = useState(false);
  const statusModal = useModal();

  const editId = rulePlanDataObj?.edit_unique_id || rulePlanDataObj?.unique_id;
  const oldPlanIsActive = rulePlanDataObj?.rule_plan_is_active;

  const { onFinish, formProps, form, saveButtonProps } = useForm({
    resource: apiEndpoint.rulesPlanDraft,
    action: 'edit',
    id: editId,
    queryOptions: { enabled: editing && !!editId },
    successNotification: false,
    errorNotification: false,
    onMutationSuccess: (data) => {
      statusModal.close();
      setRulePlanDataObj(data?.data?.results);
      setEditing(false);
      refetch();
    },
    onMutationError: (error) => {
      const message = error?.message || t('notifications.somethingError');
      openNotificationWithIcon(
        constants.error,
        t('notifications.mutationError'),
        message
      );
    },
  });

  const onEditFinishHandler = async (data: any) => {
    const values = { ...form.getFieldsValue(true), ...data };
    if (
      !statusModal.modalProps.open &&
      oldPlanIsActive !== values?.rule_plan_is_active
    ) {
      statusModal.show();
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { results: unusedResults, ...rest } = values;
    await onFinish(transformPlanValues(rest));
  };

  // Create-mode form, used only when there's no plan yet
  const {
    onFinish: onFinishCreate,
    formProps: createFormProps,
    form: createForm,
  } = useForm({
    resource: apiEndpoint.rulesPlanDraft,
    action: 'create',
    successNotification: false,
    errorNotification: false,
    redirect: false,
    onMutationSuccess: (data) => {
      setRulePlanDataObj(data?.data?.results);
    },
  });

  const { selectProps: unifiedSelectProps } = useSelect({
    resource: `${apiEndpoint.getProgrambasedUnifiedSource}/?page=1&page_size=1000`,
    dataProviderName: 'unifiedDataProvider',
    optionLabel: 'unified_source_name',
    optionValue: 'unique_id',
    meta: { type: 'select' },
  });
  const unifiedList = (unifiedSelectProps as any)?.options || [];
  const unifiedSourceLabel =
    unifiedList.find(
      (item) => item.value === rulePlanDataObj?.unified_source_master_id
    )?.label || rulePlanDataObj?.unified_source_master_name;

  // --- Send for Approval ---
  const programId = userDetails?.iprogram;
  const { data: ipData } = useOne({
    dataProviderName: 'organizationDataProvider',
    resource: apiEndpoint.incentiveProgram,
    id: programId,
    queryOptions: { enabled: !!programId },
  });
  const isRuleApproval = ipData?.data?.results?.rule_approval;

  const { listProps: approvalResponse, queryResult: approvalQueryResult } =
    useSimpleList({
      resource: `${apiEndpoint.sendRulePlanForApproval}/${rulePlanDataObj?.edit_unique_id}`,
      pagination: { mode: 'off' },
      queryOptions: { enabled: false, cacheTime: 0 },
    });
  const approvalStatus = (approvalResponse?.dataSource as any)?.status || '';

  useEffect(() => {
    if (approvalStatus === 'success') {
      openNotificationWithIcon(
        constants.success,
        t('notifications.mutationSuccess'),
        (approvalResponse?.dataSource as any)?.message ||
          'Rule Plan sent for approval'
      );
      refetch();
      list(resourceName.rulePlans);
    }
  }, [approvalStatus]);

  const handleSendForApproval = async () => {
    if (!rulePlanDataObj?.edit_unique_id) return;
    await approvalQueryResult.refetch();
  };
  // --- end Send for Approval ---

  // Create mode: render the plan form inline, nothing to summarize yet
  if (pageType === 'Create' && !rulePlanDataObj?.unique_id) {
    return (
      <>
        <div style={{ padding: '24px' }}>
          <RulePlanForm
            formData={{ form: createForm, formProps: createFormProps }}
            eventHandler={{
              onFinishHandler: (values) =>
                onFinishCreate(transformPlanValues(values)),
            }}
            stateData={{ unifiedList }}
            staticData={{ type: 'Create' }}
            configData={{ disabled: false }}
          />
          <div className="flex justify-end mt-4">
            <Button
              type="primary"
              htmlType="submit"
              form="rule-change-plan-form"
            >
              {t('rulePlans.savePlanDetails')}
            </Button>
          </div>
        </div>
        <Divider style={{ margin: 0 }} />
      </>
    );
  }

  return (
    <>
      <div style={{ padding: '16px 24px' }}>
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={16}>
            <span className="font-semibold text-lg">
              {rulePlanDataObj?.rule_plan_name}
            </span>
            <Tag
              color={rulePlanDataObj?.rule_plan_is_active ? 'green' : 'default'}
            >
              {rulePlanDataObj?.rule_plan_is_active
                ? t('rulePlans.rulePlanActive')
                : t('rulePlans.rulePlanInactive')}
            </Tag>
            <span className="text-slate-500 text-sm flex items-center gap-1">
              <CalendarOutlined />
              {dayjs(rulePlanDataObj?.rule_plan_start_date).format(
                'YYYY-MM-DD'
              )}{' '}
              -{' '}
              {dayjs(rulePlanDataObj?.rule_plan_end_date).format('YYYY-MM-DD')}
            </span>
            <span className="text-slate-500 text-sm flex items-center gap-1">
              <DatabaseOutlined />
              {unifiedSourceLabel}
            </span>
            <span className="text-slate-500 text-sm flex items-center gap-1">
              <WalletOutlined />
              {rulePlanDataObj?.rule_plan_compensation_type}
            </span>
          </Flex>
          <Flex gap={8}>
            {pageType === 'Edit' &&
            isRuleApproval &&
            rulePlanDataObj?.status === 'pending' ? (
              <Button type="primary" onClick={handleSendForApproval}>
                {t('buttons.sendForApproval')}
              </Button>
            ) : null}
            {pageType === 'Edit' ? (
              <Button
                icon={<EditOutlined />}
                onClick={() => setEditing(true)}
              />
            ) : null}
          </Flex>
        </Flex>
      </div>
      <Divider style={{ margin: 0 }} />
      {pageType === 'Edit' ? (
        <Drawer
          title={t('rulePlans.editRulePlanTitle')}
          width="70%"
          maskClosable={false}
          open={editing}
          onClose={() => {
            statusModal.close();
            setEditing(false);
          }}
          destroyOnClose
          afterOpenChange={(open) => {
            if (open && rulePlanDataObj?.rule_plan_start_date) {
              form.setFieldsValue({
                rule_plans_date: [
                  dayjs(rulePlanDataObj.rule_plan_start_date),
                  dayjs(rulePlanDataObj.rule_plan_end_date),
                ],
              });
            }
          }}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => {
                  statusModal.close();
                  setEditing(false);
                }}
              >
                {t('buttons.cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                form="rule-change-plan-form"
              >
                {t('rulePlans.savePlanDetails')}
              </Button>
            </div>
          }
        >
          <RulePlanForm
            formData={{ form, formProps }}
            eventHandler={{ onFinishHandler: onEditFinishHandler }}
            stateData={{ unifiedList }}
            staticData={{ type: 'Edit' }}
            configData={{ disabled: false }}
          />
          <CustomModal
            openModal={statusModal.modalProps.open}
            hideModal={() => statusModal.close()}
            title={t('common.note')}
            message={
              oldPlanIsActive
                ? t('rulePlans.messages.rulePlanInactiveAlert')
                : t('rulePlans.messages.rulePlanActiveAlert')
            }
            footer={getFooterButton(
              oldPlanIsActive,
              saveButtonProps,
              form,
              onEditFinishHandler,
              t
            )}
          />
        </Drawer>
      ) : null}
    </>
  );
};

export default RulePlanStrip;
